// Typing markdown makes a task list.
//
// TipTap's TaskItem input rule only fires on `[ ] ` / `[x] ` at the start of a
// paragraph. Typing `- [ ] ` fires the bullet list rule at `- ` first, and the
// default rule then nests a task list inside the bullet item (two levels).
//
// Two ways of typing are handled:
//   1. `[ ] ` / `[x] ` right after `- ` made a bullet item -> that bullet list
//      becomes a task list (not nested), so typing the markdown gives the same shape;
//   2. `-[] ` / `-[ ] ` / `-[x] ` (no space) -> a task list directly.
//
// They must run before TaskItem's rule, hence the priority (TipTap orders
// extensions by priority, and input rules in the same order).

import { Extension, InputRule, wrappingInputRule } from "@tiptap/core";

/** `[ ] ` / `[] ` / `[x] ` typed at the start of a bullet item. */
const CHECK_IN_BULLET_RE = /^\s*\[( |x)?\]\s$/;
/** `-[ ] ` / `-[] ` / `-[x] ` typed at the start of a paragraph. */
const DASH_CHECK_RE = /^\s*-\[( |x)?\]\s$/;

export const TaskListInput = Extension.create({
  name: "taskListInput",
  priority: 1000,

  addInputRules() {
    const { taskItem, taskList } = this.editor.schema.nodes;
    if (!taskItem || !taskList) return [];
    return [
      new InputRule({
        find: CHECK_IN_BULLET_RE,
        handler: ({ state, range, match, chain }) => {
          // paragraph (depth) > listItem (-1) > bulletList (-2)
          const $from = state.doc.resolve(range.from);
          if ($from.depth < 3) return null;
          const item = $from.node(-1);
          const list = $from.node(-2);
          if (
            item.type.name !== "listItem" ||
            list.type.name !== "bulletList" ||
            $from.index(-1) !== 0
          ) {
            return null;
          }
          // A list of several items (a nested one) cannot change just the
          // typed item. Top-level lists always have one item (list-blocks.ts).
          if (list.childCount !== 1) return null;
          const checked = match[1] === "x";
          const listPos = $from.before(-2);
          chain()
            .deleteRange(range)
            .command(({ tr }) => {
              // Rebuild the whole list: changing the item or the list alone
              // passes through bulletList > taskItem, which fails the content
              // check. The deleted range is inside the list, so its position
              // holds. Attributes (blockId) are kept.
              const current = tr.doc.nodeAt(listPos);
              if (!current) return false;
              const newItem = taskItem.create(
                { ...item.attrs, checked },
                current.firstChild?.content,
              );
              tr.replaceWith(
                listPos,
                listPos + current.nodeSize,
                taskList.create({ ...list.attrs }, newItem),
              );
              return true;
            })
            // Start of the paragraph's content (list +1, item +1, paragraph +1).
            .setTextSelection(listPos + 3)
            .run();
        },
      }),
      wrappingInputRule({
        find: DASH_CHECK_RE,
        type: taskItem,
        getAttributes: (match) => ({ checked: match[1] === "x" }),
      }),
    ];
  },
});
